
import React, { createContext, useState, useContext, ReactNode } from 'react';

type Direction = 'ltr' | 'rtl';
type Language = 'en' | 'he';

interface LanguageContextType {
  language: Language;
  direction: Direction;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  // Default to English, but allow override from localStorage
  const storedLanguage = localStorage.getItem('language') as Language;
  const [language, setLanguageState] = useState<Language>(storedLanguage || 'en');
  
  // Set direction based on language
  const direction: Direction = language === 'he' ? 'rtl' : 'ltr';
  
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    // Update HTML dir attribute but only for the root document
    // Individual components will handle their own RTL/LTR based on content
    document.documentElement.dir = lang === 'he' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  };
  
  // Set initial direction on mount
  React.useEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = language;
  }, [direction, language]);
  
  return (
    <LanguageContext.Provider value={{ language, direction, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
